import { api, compareSeq, extensionOf, maxSeq, type FileInfo, type Message, type Seq } from '@sd/contracts';
import type { PersistedClientState, PersistedPendingMessage, PersistedTrayItem } from '@sd/contracts/ipc';
import { ClientModule, errorCode, signOutReason, SIGN_OUT_CODES } from './context';
import { upsertMessages } from './merge';
import type { ClientState, PendingMessage, TrayItem } from './state';
import { trayStateOf } from './uploads';

/** Pending sends older than this are not resent automatically after a restart. */
export const AUTO_RESEND_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Exponential backoff for transient failures: 1s, 2s, 4s … capped at 30s. */
export function retryDelayMs(attempts: number, random: () => number = Math.random): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.max(0, attempts - 1));
  return Math.round(base * (0.8 + random() * 0.4));
}

/** Server errors worth retrying with the same clientMessageId. 4xx contract errors are final. */
export function isTransient(code: string): boolean {
  return code === 'NETWORK' || code === 'INTERNAL' || code === 'MAINTENANCE' || code === 'RATE_LIMITED' || code === 'TOKEN_EXPIRED';
}

export function toPersisted(userId: string, pending: Record<string, PendingMessage>, drafts: Record<string, string>, trays: Record<string, TrayItem[]> = {}): PersistedClientState {
  const items: PersistedPendingMessage[] = Object.values(pending)
    .filter((p) => !p.upload) // in-flight local file handles cannot be restored
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .slice(-500)
    .map((p) => ({
      clientMessageId: p.clientMessageId,
      conversationId: p.conversationId,
      text: p.text,
      replyToMessageId: p.replyToMessageId,
      attachmentIds: p.attachmentIds,
      attachments: p.attachments,
      mentions: p.mentions,
      createdAt: p.createdAt,
      attempts: p.attempts,
      state: p.state,
    }));
  const cleanDrafts: Record<string, string> = {};
  for (const [id, text] of Object.entries(drafts)) if (text.trim()) cleanDrafts[id] = text.slice(0, 10_000);
  const files: PersistedTrayItem[] = Object.values(trays)
    .flat()
    .slice(-100)
    .map((item) => ({ conversationId: item.conversationId, name: item.name.slice(0, 255), size: item.size, file: item.file }));
  return { version: 1, userId, pending: items, drafts: cleanDrafts, trays: files };
}

/**
 * Restores persisted sends after a restart. Recent items are queued for a
 * resend with the same clientMessageId (the server deduplicates, so a message
 * that was stored before the crash is not duplicated). Old ones wait for the
 * user as failed. Tray files the server has stay sendable; a file that was
 * still being sent has to be attached again, since the picked file is gone.
 */
export function fromPersisted(
  state: PersistedClientState,
  now: Date,
  newId: () => string = () => crypto.randomUUID(),
): { pending: Record<string, PendingMessage>; drafts: Record<string, string>; trays: Record<string, TrayItem[]> } {
  const pending: Record<string, PendingMessage> = {};
  for (const item of state.pending) {
    const age = now.getTime() - new Date(item.createdAt).getTime();
    const stale = age > AUTO_RESEND_WINDOW_MS;
    pending[item.clientMessageId] = {
      clientMessageId: item.clientMessageId,
      conversationId: item.conversationId,
      text: item.text,
      replyToMessageId: item.replyToMessageId,
      attachmentIds: item.attachmentIds,
      attachments: item.attachments ?? [],
      mentions: item.mentions ?? [],
      createdAt: item.createdAt,
      attempts: item.attempts,
      state: stale || item.state === 'failed' ? 'failed' : 'queued',
      lastError: stale ? 'STALE' : item.state === 'failed' ? 'FAILED_BEFORE_RESTART' : null,
      upload: null,
    };
  }
  const trays: Record<string, TrayItem[]> = {};
  for (const item of state.trays ?? []) {
    const restored: TrayItem = {
      localId: newId(),
      conversationId: item.conversationId,
      name: item.name,
      ext: extensionOf(item.name),
      size: item.size,
      state: item.file ? trayStateOf(item.file) : 'failed',
      progress: item.file ? 100 : 0,
      fileId: item.file?.id ?? null,
      file: item.file,
      error: item.file ? null : 'ATTACH_AGAIN',
    };
    (trays[item.conversationId] ??= []).push(restored);
  }
  return { pending, drafts: { ...state.drafts }, trays };
}

/** The part of the trays the saved copy holds: upload progress alone is not worth a write. */
export function savedTrayKey(trays: Record<string, TrayItem[]>): string {
  return Object.values(trays)
    .flat()
    .map((item) => `${item.localId}:${item.state}:${item.fileId ?? ''}`)
    .join();
}

const MAX_SEND_ATTEMPTS = 5;
const PERSIST_DEBOUNCE_MS = 400;

/**
 * The viewer's unconfirmed sends: ordered sending with retries, confirmation
 * by response or event, and the encrypted copy kept by the desktop host.
 */
export class Outbox extends ClientModule {
  private readonly sendLoops = new Map<string, Promise<void>>();

  async restorePersisted(userId: string) {
    const persistence = this.ctx.platform.persistence;
    if (!persistence) {
      this.set({ storage: 'memory' });
      return;
    }
    const info = await this.ctx.platform.appInfo().catch(() => null);
    if (info && info.secureStorage !== 'os') {
      this.set({ storage: 'unavailable' });
      return;
    }
    const saved = await persistence.load(this.ctx.serverId).catch(() => null);
    this.set({ storage: 'persistent' });
    if (!saved) return;
    if (saved.userId !== userId) {
      // Another account used this server on this PC: never expose its data.
      await persistence.clear(this.ctx.serverId).catch(() => {});
      return;
    }
    const restored = fromPersisted(saved, this.ctx.now(), this.ctx.newId);
    this.set({ pending: restored.pending, drafts: restored.drafts, trays: restored.trays });
  }

  schedulePersist() {
    if (this.state.storage !== 'persistent') return;
    this.timers.after('persist', PERSIST_DEBOUNCE_MS, () => void this.persistNow());
  }

  /** Saves pending sends, drafts and trays at once, e.g. before the client stops; resolves once main has them. */
  persistNow(): Promise<void> {
    const persistence = this.ctx.platform.persistence;
    const me = this.state.me;
    if (!persistence || this.state.storage !== 'persistent' || !me) return Promise.resolve();
    this.timers.clear('persist');
    return persistence
      .save(this.ctx.serverId, toPersisted(me.id, this.state.pending, this.state.drafts, this.state.trays))
      .then((result) => {
        if (!result.persisted && this.state.storage === 'persistent') {
          this.set({ storage: 'unavailable' });
          this.ctx.notice({ kind: 'storage.unavailable' });
        }
      })
      .catch(() => {});
  }

  /**
   * Sends over REST whatever the socket state. While an earlier message of the
   * conversation waits (offline, retry timer), the new one waits behind it.
   */
  sendMessage(conversationId: string, text: string, options: { replyToMessageId?: string | null; attachments?: FileInfo[]; mentions?: string[] } = {}): string {
    const clientMessageId = this.ctx.newId();
    const attachments = options.attachments ?? [];
    const held = this.underMaintenance() || Object.values(this.state.pending).some((p) => p.conversationId === conversationId && (p.state === 'queued' || this.timers.has(`retry:${p.clientMessageId}`)));
    const pending: PendingMessage = {
      clientMessageId,
      conversationId,
      text,
      replyToMessageId: options.replyToMessageId ?? null,
      attachmentIds: attachments.map((f) => f.id),
      attachments,
      mentions: options.mentions ?? [],
      createdAt: this.ctx.now().toISOString(),
      attempts: 0,
      state: held ? 'queued' : 'sending',
      lastError: null,
      upload: null,
    };
    this.set((s) => ({ pending: { ...s.pending, [clientMessageId]: pending }, drafts: text ? { ...s.drafts, [conversationId]: '' } : s.drafts }));
    void this.sendInOrder(conversationId);
    return clientMessageId;
  }

  /**
   * Sends the conversation's pending messages oldest first. One that has to
   * wait (offline, retry timer) holds back the later ones so nothing
   * overtakes it; failed ones are skipped until the user retries them.
   */
  private sendInOrder(conversationId: string): Promise<void> {
    const running = this.sendLoops.get(conversationId);
    if (running) return running;
    const loop = this.sendLoop(conversationId);
    this.sendLoops.set(conversationId, loop);
    return loop;
  }

  private async sendLoop(conversationId: string): Promise<void> {
    // Yield once so the loop is registered before it can end.
    await Promise.resolve();
    try {
      for (let next = this.nextToSend(conversationId); next && !this.stopped; next = this.nextToSend(conversationId)) {
        if (!this.underMaintenance() && !this.timers.has(`retry:${next.clientMessageId}`) && (await this.doSend(next.clientMessageId))) continue;
        if (this.underMaintenance()) this.holdForMaintenance(conversationId);
        else this.holdBehind(next);
        return;
      }
    } finally {
      this.sendLoops.delete(conversationId);
    }
  }

  private underMaintenance(): boolean {
    return this.state.serverInfo?.state === 'MAINTENANCE';
  }

  /** Oldest message of the conversation still to be sent. One whose upload still runs is not sendable yet. */
  private nextToSend(conversationId: string): PendingMessage | undefined {
    let next: PendingMessage | undefined;
    for (const p of Object.values(this.state.pending)) {
      if (p.conversationId !== conversationId || p.state === 'failed' || p.upload) continue;
      if (!next || p.createdAt < next.createdAt) next = p;
    }
    return next;
  }

  /** During maintenance every message of the conversation waits, and its bubble says what for. */
  private holdForMaintenance(conversationId: string) {
    this.set((s) => {
      const held = Object.values(s.pending).filter((p) => p.conversationId === conversationId && p.state !== 'failed' && !p.upload);
      if (!held.length) return {};
      const pending = { ...s.pending };
      for (const p of held) pending[p.clientMessageId] = { ...p, state: 'queued', lastError: 'MAINTENANCE' };
      return { pending };
    });
  }

  /** Messages waiting behind `head` show as queued, not as sending. */
  private holdBehind(head: PendingMessage) {
    this.set((s) => {
      const behind = Object.values(s.pending).filter((p) => p.conversationId === head.conversationId && p.clientMessageId !== head.clientMessageId && p.state === 'sending' && !p.upload);
      if (!behind.length) return {};
      const pending = { ...s.pending };
      for (const p of behind) pending[p.clientMessageId] = { ...p, state: 'queued' };
      return { pending };
    });
  }

  private patchPending(clientMessageId: string, patch: Partial<PendingMessage>) {
    this.set((s) => {
      const p = s.pending[clientMessageId];
      return p ? { pending: { ...s.pending, [clientMessageId]: { ...p, ...patch } } } : {};
    });
  }

  removePending(clientMessageId: string) {
    const removed = this.state.pending[clientMessageId];
    this.clearRetry(clientMessageId);
    if (!removed) return;
    this.set((s) => {
      const pending = { ...s.pending };
      delete pending[clientMessageId];
      return { pending };
    });
    // Whatever waited behind it may go now.
    void this.sendInOrder(removed.conversationId);
  }

  private clearRetry(clientMessageId: string) {
    this.timers.clear(`retry:${clientMessageId}`);
  }

  /** Sends one message. False when it has to wait, which holds back the later ones. */
  private async doSend(clientMessageId: string): Promise<boolean> {
    const pending = this.state.pending[clientMessageId];
    if (!pending) return true;
    this.patchPending(clientMessageId, { state: 'sending', attempts: pending.attempts + 1 });
    try {
      const res = await this.api.call(api.messages.send, {
        params: { conversationId: pending.conversationId },
        body: {
          clientMessageId,
          text: pending.text,
          attachmentIds: pending.attachmentIds,
          ...(pending.replyToMessageId ? { replyToMessageId: pending.replyToMessageId } : {}),
          mentions: pending.mentions,
        },
      });
      this.acceptSent(res.message);
      this.removePending(clientMessageId);
      return true;
    } catch (error) {
      const code = errorCode(error);
      if (SIGN_OUT_CODES.has(code) && code !== 'TOKEN_EXPIRED') {
        this.patchPending(clientMessageId, { state: 'queued', lastError: code });
        this.ctx.signOut(signOutReason(code));
        return false;
      }
      if (code === 'IDEMPOTENCY_MISMATCH') {
        const copy = await this.findStoredCopy(pending).catch(() => null);
        if (copy) {
          this.acceptSent(copy);
          this.removePending(clientMessageId);
          return true;
        }
      }
      if (code === 'MAINTENANCE') {
        // However long it lasts: the messages go out once the server is ACTIVE again.
        this.patchPending(clientMessageId, { state: 'queued', lastError: code });
        this.set((s) => (s.serverInfo ? { serverInfo: { ...s.serverInfo, state: 'MAINTENANCE' } } : {}));
        return false;
      }
      const attempts = (this.state.pending[clientMessageId]?.attempts ?? 1);
      if (code === 'NETWORK') {
        this.patchPending(clientMessageId, { state: 'queued', lastError: code });
        // Known offline, the reconnect flushes it; otherwise (connecting, syncing, a flaky network) nothing else would.
        if (this.state.connection !== 'offline') this.scheduleRetry(clientMessageId, attempts);
        return false;
      }
      if (isTransient(code) && attempts < MAX_SEND_ATTEMPTS) {
        this.patchPending(clientMessageId, { lastError: code });
        this.scheduleRetry(clientMessageId, attempts);
        return false;
      }
      this.patchPending(clientMessageId, { state: 'failed', lastError: code });
      return true;
    }
  }

  /**
   * The server already stored a message under this clientMessageId with other
   * content: an app that persisted the outbox without mentions resent it after
   * a crash. That stored copy is the message, so look for it among the
   * viewer's recent messages instead of failing forever.
   */
  private async findStoredCopy(pending: PendingMessage): Promise<Message | null> {
    // Allow for the PC clock being off when deciding that a page is older than the send.
    const since = new Date(pending.createdAt).getTime() - 10 * 60_000;
    let before: Seq | undefined;
    for (let page = 0; page < 10; page += 1) {
      const res = await this.api.call(api.messages.list, { params: { conversationId: pending.conversationId }, query: { limit: 100, ...(before ? { before } : {}) } });
      const copy = res.messages.find((m) => m.clientMessageId === pending.clientMessageId);
      if (copy) return copy;
      const oldest = res.messages[0];
      if (!oldest || !res.hasMoreBefore || new Date(oldest.createdAt).getTime() < since) return null;
      before = oldest.seq;
    }
    return null;
  }

  private scheduleRetry(clientMessageId: string, attempts: number) {
    this.timers.after(`retry:${clientMessageId}`, retryDelayMs(attempts), () => {
      const pending = this.state.pending[clientMessageId];
      if (pending) void this.sendInOrder(pending.conversationId);
    });
  }

  private acceptSent(message: Message) {
    const id = message.conversationId;
    this.set((s) => {
      const t = s.timelines[id];
      const c = s.conversations[id];
      const next: Partial<ClientState> = {};
      if (t?.loaded && !t.hasMoreAfter) next.timelines = { ...s.timelines, [id]: { ...t, messages: upsertMessages(t.messages, [message]) } };
      if (c && compareSeq(message.seq, c.lastSeq) >= 0) {
        next.conversations = {
          ...s.conversations,
          [id]: {
            ...c,
            lastSeq: message.seq,
            lastActivityAt: message.createdAt > c.lastActivityAt ? message.createdAt : c.lastActivityAt,
            lastMessage: {
              messageId: message.id,
              senderId: message.senderId,
              kind: !message.text?.trim() && message.attachments.length ? 'attachment' : 'text',
              text: message.text?.slice(0, 140) ?? null,
              system: null,
              attachmentName: message.attachments[0]?.name ?? null,
              attachmentCount: message.attachments.length,
              createdAt: message.createdAt,
            },
            my: { ...c.my, lastReadSeq: maxSeq(c.my.lastReadSeq, message.seq) },
          },
        };
      }
      return next;
    });
  }

  /** Sends everything still pending now, each conversation oldest first. Resolves with how many were queued. */
  async flushQueue(): Promise<number> {
    const waiting = Object.values(this.state.pending).filter((p) => p.state !== 'failed' && !p.upload);
    // The connection is back: no need to sleep out a retry backoff.
    for (const p of waiting) this.clearRetry(p.clientMessageId);
    const queued = waiting.filter((p) => p.state === 'queued').length;
    await Promise.all([...new Set(waiting.map((p) => p.conversationId))].map((id) => this.sendInOrder(id)));
    return queued;
  }

  /** "다시 보내기": same clientMessageId, so a stored-but-unacknowledged send is not duplicated. */
  retry(clientMessageId: string) {
    const p = this.state.pending[clientMessageId];
    if (!p) return;
    this.patchPending(clientMessageId, { state: 'sending', lastError: null, attempts: 0 });
    void this.sendInOrder(p.conversationId);
  }

  discard(clientMessageId: string) {
    this.removePending(clientMessageId);
  }
}
